/**
 * Application Constants
 * ======================
 * Centralized constants for the application
 */

/**
 * Default (platform) brand name.
 *
 * zCortexAI is the product/platform; individual organizations (e.g. Zamanat,
 * Zignally) are tenants and supply their own display name via org branding.
 * This constant is the single source of truth for the brand shown when no org
 * is in context (the plain `/login` experience and the org-less super-admin
 * shell). It is intentionally hardcoded rather than read from REACT_APP_NAME,
 * which is set to a tenant name and would otherwise re-introduce inconsistent
 * branding.
 */
export const DEFAULT_BRAND_NAME = 'zCortexAI';

/**
 * User Roles
 */
export const ROLES = {
  ADMIN: 'admin',
  BD_TEAM: 'bd_team',
  COMPLIANCE_OFFICER: 'compliance_officer',
  VIEWER: 'viewer',
};

/**
 * Role Display Names
 */
export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.BD_TEAM]: 'BD Team',
  [ROLES.COMPLIANCE_OFFICER]: 'Compliance Officer',
  [ROLES.VIEWER]: 'Viewer',
};

/**
 * Role Descriptions
 */
export const ROLE_DESCRIPTIONS = {
  [ROLES.ADMIN]: 'Full system access including proposal approval and user management',
  [ROLES.BD_TEAM]: 'Full lead management, proposal generation and submission for approval, analysis',
  [ROLES.COMPLIANCE_OFFICER]: 'View leads and proposals, manage Brain, access user management',
  [ROLES.VIEWER]: 'Read-only access to leads, proposals, meetings, and dashboards',
};

/**
 * Role Priority (for primary role selection)
 */
export const ROLE_PRIORITY = {
  [ROLES.ADMIN]: 1,
  [ROLES.COMPLIANCE_OFFICER]: 2,
  [ROLES.BD_TEAM]: 3,
  [ROLES.VIEWER]: 4,
};

/**
 * Human-friendly permission labels
 */
export const PERMISSION_LABELS = {
  '*': 'All Permissions',
  'leads:create': 'Create Leads',
  'leads:read': 'View Leads',
  'leads:update': 'Edit Leads',
  'leads:delete': 'Delete Leads',
  'discovery:submit': 'Submit Discovery',
  'discovery:read': 'View Discovery',
  'proposals:send': 'Send Proposals',
  'proposals:read': 'View Proposals',
  'proposals:generate': 'Generate Proposals',
  'proposals:submit_for_review': 'Submit for Review',
  'proposals:review': 'Review Proposals',
  'proposals:approve': 'Approve Proposals',
  'analysis:read': 'View Analysis',
  'analysis:update': 'Edit Analysis',
  'knowledge_base:manage': 'Manage Brain',
  'compliance:create': 'Create Compliance Records',
  'compliance:read': 'View Compliance',
  'compliance:update': 'Edit Compliance',
  'audit_logs:read': 'View Audit Logs',
  'dashboard:read': 'View Dashboard',
  'users:read': 'View Users',
  'users:create': 'Create Users',
  'users:update': 'Edit Users',
  'users:delete': 'Delete Users',
  'roles:manage': 'Manage Roles',
  'meetings:read': 'View Meetings',
  'system:admin': 'System Administration',
  'system:read': 'View System Settings',
};

/**
 * Role Permissions (for display in user forms)
 */
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: ['*'],
  [ROLES.BD_TEAM]: [
    'leads:create', 'leads:read', 'leads:update',
    'discovery:submit', 'proposals:send', 'proposals:read',
    'proposals:generate', 'proposals:submit_for_review',
    'analysis:read', 'analysis:update', 'dashboard:read',
  ],
  [ROLES.COMPLIANCE_OFFICER]: [
    'leads:read', 'proposals:read',
    'knowledge_base:manage',
    'system:read', 'system:admin',
    'users:read', 'dashboard:read',
  ],
  [ROLES.VIEWER]: [
    'leads:read', 'proposals:read', 'meetings:read',
    'analysis:read', 'dashboard:read',
  ],
};

/**
 * User Status
 */
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

/**
 * Pagination Defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PER_PAGE: 20,
  MAX_PER_PAGE: 100,
};

/**
 * Sort Options
 */
export const SORT_OPTIONS = {
  CREATED_AT: 'created_at',
  LAST_LOGIN: 'last_login',
  EMAIL: 'email',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
};

/**
 * Sort Order
 */
export const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc',
};

/**
 * Password Requirements
 */
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 12,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true,
  REQUIRE_SPECIAL: true,
};

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  ISO: 'YYYY-MM-DDTHH:mm:ss',
};

/**
 * API Response Codes
 */
export const API_ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
};

